/**
 * zCRM configuration: the stage map, repurposed-field values and naming conventions.
 * Replaces Atomic CRM's Supabase `configuration` table.
 * Source: Discovery Handoff, "Start here: repurposed stored fields" and "Migration".
 */

/**
 * Sales stages, in OnSight PROS's own words (their Pipedrive "Sales 2.0" pipeline), stored on
 * Sales tasks as Task.Status (zInspector's status list is fixed, so each stage borrows one).
 * - The stage is where a deal is in the funnel; its outcome is separate: a deal is open, won,
 *   lost or snoozed (`end` stages). Won = the agreement is signed; onboarding then runs on its
 *   own Onboarding task, not on the sales board.
 * - Pipedrive's "Revive" is a deal type (Reactivation), "Individual landlord lead" is the
 *   account kind, "Not in current market" is a loss reason.
 * - `limitDays`: a deal is flagged once it has been in a stage longer than 3 in 4 deals took in
 *   Pipedrive (deals export, Oct 2026). `nextStep`: what zCRM suggests when a deal enters it.
 */
export const STAGES = [
  {
    id: "new_lead",
    label: "New lead",
    status: "New",
    limitDays: 5,
    limitHelp: "Inbound or chat lead, until someone qualifies it",
    nextStep: { text: "Qualify the lead and offer a demo", inDays: 1 },
  },
  {
    id: "schedule_demo",
    label: "Schedule demo",
    status: "In Progress",
    limitDays: 5,
    limitHelp: "Qualified, the demo still to book",
    nextStep: { text: "Book the demo", inDays: 2 },
  },
  {
    id: "demo_scheduled",
    label: "Demo scheduled",
    status: "Bid Requested",
    limitDays: 7,
    limitHelp: "Until the demo happens",
    nextStep: { text: "Hold the demo", inDays: 7 },
  },
  {
    id: "demo_completed",
    label: "Demo completed",
    status: "Bid(s) Received",
    limitDays: 5,
    limitHelp:
      "Demo to agreement sent. Typical created to sent: 5 days, 3 in 4 within 13",
    nextStep: { text: "Send the PROS Client Agreement", inDays: 1 },
  },
  {
    id: "agreement_sent",
    label: "Agreement sent",
    status: "Approval Requested",
    limitDays: 5,
    limitHelp:
      "Next steps and agreement sent. Typical sent to signed: 1 day, 3 in 4 within 5",
    nextStep: { text: "Follow up on the agreement", inDays: 3 },
  },
  {
    id: "awaiting_signature",
    label: "Awaiting signature",
    status: "Approval Received",
    limitDays: 5,
    limitHelp: "Agreed, the signature still to come",
    nextStep: { text: "Chase the signature", inDays: 2 },
  },
  { id: "won", label: "Won", status: "Closed", limitDays: null, end: true },
  { id: "lost", label: "Lost", status: "Canceled", limitDays: null, end: true },
  {
    id: "snoozed",
    label: "Snoozed",
    status: "Deferred",
    limitDays: null,
    end: true,
  },
] as const;

/** From this stage on, a deal needs the account's doors (it's qualified). */
export const DOORS_REQUIRED_FROM_STAGE = "schedule_demo";
// limitDays: stage time limits for the "stalled" badge. Open question for the OSP sales lead.

export const stageByStatus = (status: string) =>
  STAGES.find((s) => s.status === status);
export const statusByStage = (id: string) =>
  STAGES.find((s) => s.id === id)?.status;

/** Deal type → Task.EntranceInstruction */
export const DEAL_TYPES = [
  { id: "new_client", label: "New client", stored: "Vacant / Anytime" },
  { id: "reactivation", label: "Reactivation", stored: "Contact Tenant" },
  {
    id: "existing_client",
    label: "Existing client",
    stored: "Scheduled Appointment",
  },
] as const;

/** Product → Task.Key (per vendor; OSP shown) */
export const PRODUCTS = [
  { id: "osp", label: "OnSight PROS", stored: "Lockbox" },
  { id: "mywalkthru", label: "MyWalkThru", stored: "Pick-Up" },
  { id: "other", label: "Other", stored: "Other" },
] as const;

/** Loss reason → Task.KeyDesc (free text; the CRM offers only these) */
export const LOSS_REASONS = [
  "Unresponsive (ghosted)",
  "Does inspections in-house",
  "Not in our market",
  "Failure to launch",
  "Price",
  "Service issues",
  "Other",
] as const;

/** Contact role → Contact.TaxId (free text; the CRM offers only these) */
export const CONTACT_ROLES = [
  "Owner/Principal",
  "Operations lead",
  "Property manager",
  "Admin/Orders",
  "Other",
] as const;

/** Other repurposed fields */
export const FIELD_MAP = {
  dealValue: "AuthorizedLimit", // text; parse to a number in the data provider
  dealNotes: "WorkOrderInstructions",
  bookedAtBooth: "Pets", // boolean
  lossReason: "KeyDesc",
  contactRole: "TaxId",
  marketingConsent: "Is1099Required", // true = subscribed
  decisionMaker: "IsPrimary", // NOT the lease's main contact; that is Lease.PrimaryTenant
} as const;

/** Task types the CRM uses (Task.Action) */
export const TASK_TYPES = {
  deal: "Sales",
  lead: "Prospect",
  meeting: "Follow-up",
  implementation: "Onboarding",
  ticket: "Support",
} as const;

/**
 * Task-type pks for POST /api/task/create/ (Action takes the pk; edit takes the name).
 * Read from OSP's account on 2026-10-08. All five are switched on (Sales, Prospect and Follow-up were enabled Oct 8).
 */
export const TASK_TYPE_PK = {
  Sales: 45,
  Prospect: 70,
  "Follow-up": 104,
  Onboarding: 43,
  Support: 40,
} as const;

/**
 * GET /api/tasks/ does not return the repurposed deal fields (EntranceInstruction, Key, KeyDesc,
 * AuthorizedLimit, WorkOrderInstructions, Pets, TenantNames, PhoneNumber, Email).
 * Only GET /api/tasks/{id}/ does, so the data provider fetches details per deal and caches them.
 */
export const TASK_DETAIL_ONLY_FIELDS = [
  "EntranceInstruction",
  "EntranceInstructionDesc",
  "Key",
  "KeyDesc",
  "Pets",
  "PetsDesc",
  "Notes",
  "WorkOrderInstructions",
  "WorkOrderInstructionsHtml",
  "AuthorizedLimit",
  "TenantNames",
  "PhoneNumber",
  "Email",
  "IsBidOnly",
  "IncludeCalendar",
] as const;

/**
 * Naming conventions (see the spec's "Test data and naming convention" section).
 *
 * User groups named "CRM – <Market>" hold accounts. The prefix marks a property as an
 * account (keeps it out of inspection lists); the rest is shown as the market.
 *   "CRM – Denver Metro"  →  account, market "Denver Metro"
 */
export const ACCOUNT_GROUP_PREFIX = "CRM – ";

/**
 * Extra groups treated as account groups. The dashboard's property form only offers existing
 * groups, so the Oct 8 test accounts live in OSP's existing "Test" group (market shown as "Test").
 */
export const EXTRA_ACCOUNT_GROUPS = ["Test"] as const;

export function parseAccountGroup(groupName: string): {
  isAccount: boolean;
  market: string | null;
} {
  if ((EXTRA_ACCOUNT_GROUPS as readonly string[]).includes(groupName))
    return { isAccount: true, market: groupName };
  if (!groupName.startsWith(ACCOUNT_GROUP_PREFIX))
    return { isAccount: false, market: null };
  return {
    isAccount: true,
    market: groupName.slice(ACCOUNT_GROUP_PREFIX.length).trim(),
  };
}

/** Test records carry this AccountingID / ExternalReferenceNumber prefix, so they can be found and deleted later. */
export const TEST_ID_PREFIX = "ZT-";
export const isTestRecord = (id?: string | null) =>
  !!id && id.startsWith(TEST_ID_PREFIX);

/**
 * Account details: the zData rows of the property's "Account" area (one detail per field).
 * `detail` must match the detail name in zInspector exactly (Areas → Account). Chosen from the
 * Pipedrive organizations export: the fields OSP fills, plus the facts its notes record
 * (doors served, PM software, referrals). See zcrm-docs/reference/account-fields.md.
 */
export const ACCOUNT_DETAILS = [
  { key: "account_kind", detail: "Account kind" },
  { key: "health", detail: "Health segment" },
  { key: "office_phone", detail: "Office phone" },
  { key: "website", detail: "Website" },
  { key: "email", detail: "Email" },
  { key: "serviceable_doors", detail: "Serviceable doors" },
  { key: "doors_served", detail: "Doors served" },
  { key: "expected_monthly_orders", detail: "Expected monthly orders" },
  { key: "markets_served", detail: "Markets served" },
  { key: "pm_software", detail: "PM software" },
  { key: "lead_source", detail: "Lead source" },
  { key: "referred_by", detail: "Referred by" },
  { key: "former_names", detail: "Former names" },
  { key: "pipedrive_id", detail: "Pipedrive ID" },
  { key: "notes", detail: "Notes" },
] as const;

export type AccountDetailKey = (typeof ACCOUNT_DETAILS)[number]["key"];

/** Account kind → zData "Account kind" (Pipedrive Label: PM / Individual). */
export const ACCOUNT_KINDS = [
  "PM company",
  "Individual landlord",
  "Other",
] as const;

/**
 * Health → zData "Health segment", set by hand in zInspector until Pulse computes it
 * (Pipedrive: the "At Risk Clients" / "Inactive Client" columns of the CS pipeline).
 */
export const HEALTH_SEGMENTS = [
  { value: "Healthy", tone: "success" },
  { value: "Watch", tone: "warning" },
  { value: "At risk", tone: "error" },
  { value: "Inactive", tone: "neutral" },
  { value: "Won back", tone: "info" },
] as const;

/**
 * Account status. By default it comes from the account's engagement (lease): no lease →
 * prospect; Moving In → onboarding (signed, not ordering yet); Current → active; Moving Out →
 * winding down; only inactive leases → former client.
 * The team can also set it by hand (it's how they used Pipedrive's client columns), which is
 * the only way to say "at risk" or "inactive" (still under contract, not ordering). A status set
 * by hand is stored on the account's journal task (ACCOUNT_STATUS_TASK_FIELD) and wins until
 * it's set back to automatic.
 */
export const ACCOUNT_STATUSES = [
  { id: "onboarding", label: "Onboarding", view: "clients", tone: "warning" },
  { id: "active", label: "Active", view: "clients", tone: "success" },
  { id: "at_risk", label: "At risk", view: "clients", tone: "error" },
  { id: "inactive", label: "Inactive", view: "clients", tone: "neutral" },
  {
    id: "winding_down",
    label: "Winding down",
    view: "clients",
    tone: "neutral",
  },
  { id: "prospect", label: "Prospect", view: "prospects", tone: "info" },
  { id: "former", label: "Former client", view: "former", tone: "neutral" },
] as const;

/**
 * The journal task field that holds a status set by hand (an ACCOUNT_STATUSES id). It has to be
 * a field `GET /api/tasks/` returns (the list leaves out the detail fields), so the accounts
 * list can read it without one request per account.
 */
export const ACCOUNT_STATUS_TASK_FIELD = "ExternalReferenceNumber";

export type AccountStatusId = (typeof ACCOUNT_STATUSES)[number]["id"];

/** Lease statuses in the order they decide an account's status when it has several leases. */
export const LEASE_STATUS_TO_ACCOUNT_STATUS: [string, AccountStatusId][] = [
  ["Current", "active"],
  ["Current - Renewal", "active"],
  ["Moving In", "onboarding"],
  ["Moving Out", "winding_down"],
  ["Listed", "prospect"],
];

/** The kind given to a next step set from the accounts table or the account page. */
export const NEXT_STEP_KIND = "To-do";

/** Days from signature to first order the business aims for (activation; handoff "The job"). */
export const ACTIVATION_TARGET_DAYS = 30;

/**
 * Each account has one journal task: a Follow-up task named "Account journal — <account>",
 * whose manager is the account manager and whose thread is the account's timeline.
 */
export const JOURNAL_TASK_PREFIX = "Account journal";

/**
 * Commercial terms: the zData rows of the property's "Commercial" area, set per client (prices
 * vary by deal and city). `detail` must match the detail name in zInspector exactly.
 * Today OSP prices per inspection, with one price for move-in / move-out and one for periodic
 * inspections; a monthly plan is being considered. See zcrm-docs/reference/account-fields.md.
 */
export const COMMERCIAL_AREA = "Commercial";

export const COMMERCIAL_TERMS = [
  { key: "pricing_model", detail: "Pricing model" },
  { key: "price_mimo", detail: "Move-in/move-out price" },
  { key: "price_periodic", detail: "Periodic price" },
  { key: "plan_name", detail: "Plan name" },
  { key: "plan_monthly_fee", detail: "Plan monthly fee" },
  { key: "plan_included", detail: "Plan inspections included" },
  { key: "plan_extra_price", detail: "Plan extra inspection price" },
  { key: "payment_days", detail: "Days to pay" },
  { key: "invoicing", detail: "Invoicing" },
  { key: "terms_effective", detail: "Terms effective from" },
  { key: "terms_notes", detail: "Terms notes" },
] as const;

export type CommercialTermKey = (typeof COMMERCIAL_TERMS)[number]["key"];

export const PRICING_MODELS = {
  perInspection: "Per inspection",
  plan: "Plan",
} as const;

export const INVOICING_OPTIONS = ["Per inspection", "Monthly"] as const;

/**
 * Calls, meetings and comments: Follow-up tasks on the account property (the journal task
 * excluded). Tasks are the one object the API key can write, so these work on the live API.
 * Stored as: kind → `EntranceInstruction`, title → `Description`, when → `StartDate` /
 * `EndDate` (with `IncludeCalendar`, so zInspector syncs them to Google Calendar), client
 * contact → `AssignedToContact`, notes → `Notes`, transcript and summary →
 * `WorkOrderInstructions`, recording source → `Key`. Upcoming = an open task; done = `Closed`.
 */
export const ACTIVITY_KINDS = ["Call", "Meeting", "To-do", "Comment"] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

/** Where a recording or transcript came from. */
export const ACTIVITY_SOURCES = [
  "Recorded in zCRM",
  "Uploaded recording",
  "Pasted transcript",
  "Zoom",
  "Google Meet",
  "Phone",
] as const;

export const ACTIVITY_DONE_STATUS = "Closed";
/** A meeting that didn't happen (no-show). */
export const ACTIVITY_CANCELED_STATUS = "Canceled";
export const ACTIVITY_OPEN_STATUS = "New";
/** Default length of a call or meeting on the calendar. */
export const ACTIVITY_DEFAULT_MINUTES = 30;

/**
 * The account facts the team relies on (from the Pipedrive export: FileMaker ID was filled on
 * 7% of client organizations, market on 57%, doors on 37%). An account missing any of these
 * shows in the "Needs info" view. Prospects aren't expected to have a FileMaker ID or a main
 * contact yet.
 */
export const REQUIRED_ACCOUNT_INFO = [
  { key: "market", label: "market", forProspects: true },
  { key: "account_kind", label: "account kind", forProspects: true },
  { key: "serviceable_doors", label: "doors", forProspects: true },
  { key: "accounting_id", label: "FileMaker ID", forProspects: false },
  { key: "main_contact_name", label: "main contact", forProspects: false },
] as const;

/** How far back "last touch" looks at closed tasks (logged calls, won deals, closed tickets). */
export const LAST_TOUCH_LOOKBACK_DAYS = 365;

/**
 * When a deal entered its current stage. zInspector keeps no stage-change date, so zCRM writes
 * one on every stage move, in this deal field (detail-only, so it comes with the deal's detail
 * read). Deals moved in the dashboard fall back to their last change.
 */
export const STAGE_SINCE_TASK_FIELD = "PetsDesc";

/** A deal is "gone quiet" after this many days without a change, call, meeting or note. */
export const QUIET_AFTER_DAYS = 7;

/** Default snooze length, in days. */
export const SNOOZE_DAYS = 14;

/**
 * Winning a deal starts onboarding: an Onboarding task on the account. Set the zInspector
 * process (playbook) id here to attach its steps (`todoListPk` on task create); null = no steps.
 */
export const ONBOARDING_PROCESS_PK: number | null = null;

/** The zInspector dashboard, for the things only it can do (creating a lease). */
export const ZINSPECTOR_DASHBOARD_URL = "https://portfolio.zinspector.com/";
